import {
  BarChart3,
  BookOpen,
  Boxes,
  CalendarRange,
  ClipboardList,
  ChefHat,
  ClipboardCheck,
  FileInput,
  History,
  LayoutDashboard,
  ReceiptText,
  ScrollText,
  Settings,
  Truck,
  type LucideIcon,
} from 'lucide-react';
import { canOpenArea, type AdminArea } from '@enp/domain/permissions';

export interface NavItem {
  to:
    | '/'
    | '/catalogue'
    | '/stock'
    | '/approvals'
    | '/sales'
    | '/production'
    | '/recipes'
    | '/purchasing'
    | '/planning'
    | '/orders'
    | '/imports'
    | '/replay'
    | '/reports'
    | '/settings';
  label: string;
  icon: LucideIcon;
  /** Second key of the "g then <key>" shortcut. */
  shortcut: string;
  group: 'run' | 'setup';
  /** Its read rule on the server ({@link canOpenArea}): hidden from those who can't open it. */
  area: AdminArea;
}

export const NAV: NavItem[] = [
  {
    to: '/',
    label: 'Dashboard',
    icon: LayoutDashboard,
    shortcut: 'd',
    group: 'run',
    area: 'dashboard',
  },
  { to: '/stock', label: 'Stock', icon: Boxes, shortcut: 's', group: 'run', area: 'stock' },
  // Counts and write-offs above the threshold, applied at a station, waiting for a manager.
  {
    to: '/approvals',
    label: 'Approvals',
    icon: ClipboardCheck,
    shortcut: 'v',
    group: 'run',
    area: 'approvals',
  },
  { to: '/sales', label: 'Sales', icon: ReceiptText, shortcut: 'a', group: 'run', area: 'sales' },
  {
    to: '/production',
    label: 'Production',
    icon: ChefHat,
    shortcut: 'p',
    group: 'run',
    area: 'production',
  },
  {
    to: '/purchasing',
    label: 'Purchasing',
    icon: Truck,
    shortcut: 'u',
    group: 'run',
    area: 'purchasing',
  },
  {
    to: '/planning',
    label: 'Planning',
    icon: CalendarRange,
    shortcut: 'l',
    group: 'run',
    area: 'planning',
  },
  // Customer orders (custom cakes, wholesale) with promised times, and whether they're covered.
  {
    to: '/orders',
    label: 'Orders',
    icon: ClipboardList,
    shortcut: 'o',
    group: 'run',
    area: 'orders',
  },
  {
    to: '/reports',
    label: 'Reports',
    icon: BarChart3,
    shortcut: 'r',
    group: 'run',
    area: 'reports',
  },
  {
    to: '/catalogue',
    label: 'Catalogue',
    icon: BookOpen,
    shortcut: 'c',
    group: 'setup',
    area: 'catalogue',
  },
  // Recipes and their costs are definitions the account sets up and the baker signs off,
  // rarely changed; batches (what the kitchen makes each day) are under Production.
  {
    to: '/recipes',
    label: 'Recipes',
    icon: ScrollText,
    shortcut: 'e',
    group: 'setup',
    area: 'recipes',
  },
  {
    to: '/imports',
    label: 'Imports',
    icon: FileInput,
    shortcut: 'i',
    group: 'setup',
    area: 'imports',
  },
  // The replay proves an imported catalogue against the source's past sales before going
  // live: a set-up step that follows the import, not a recurring report.
  {
    to: '/replay',
    label: 'Sales replay',
    icon: History,
    shortcut: 'y',
    group: 'setup',
    area: 'replay',
  },
  {
    to: '/settings',
    label: 'Settings',
    icon: Settings,
    shortcut: 't',
    group: 'setup',
    area: 'settings',
  },
];

export const NAV_GROUPS: { id: NavItem['group']; label: string }[] = [
  { id: 'run', label: 'Run the business' },
  { id: 'setup', label: 'Set up' },
];

/**
 * The sections someone holding `permissions` may open (D-070); all of them while the
 * permissions aren't known yet (`undefined`: /me still loading), so the menu doesn't flash.
 */
export function navFor(permissions: readonly string[] | undefined): NavItem[] {
  return permissions === undefined
    ? NAV
    : NAV.filter((item) => canOpenArea(permissions, item.area));
}

/** Route for a "g then <key>" shortcut, if any, among the sections someone may open. */
export function routeForShortcut(
  key: string,
  permissions?: readonly string[],
): NavItem['to'] | undefined {
  return navFor(permissions).find((item) => item.shortcut === key.toLowerCase())?.to;
}

/** The section a path belongs to (its first segment), if it is one of the menu's. */
export function sectionForPath(pathname: string): NavItem | undefined {
  const first = '/' + (pathname.split('/').filter(Boolean)[0] ?? '');
  return NAV.find((item) => item.to === first);
}
